import { ThemeEvents } from '@theme/events';

/**
 * Price shown inside the Add to Pack button ("Add to Pack — $24.00"), multiplied by the chosen
 * quantity. The server renders the unit price for the selected variant; when a variant change
 * morphs the button, the unit price or the text changes and the total is recalculated.
 */
class AddPrice extends HTMLElement {
  static observedAttributes = ['data-unit-cents'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** Re-applies the total when a morph resets the text to the unit price. */
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const form = this.closest('product-form-component');
    form?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#render, { signal });
    form?.addEventListener('input', this.#render, { signal });
    form?.addEventListener('change', this.#render, { signal });
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
    this.#render();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer.disconnect();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#render();
  }

  #render = () => {
    const unit = Number(this.dataset.unitCents);
    if (!Number.isFinite(unit)) return;
    const input = this.closest('product-form-component')?.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, Number(input instanceof HTMLInputElement ? input.value : 1) || 1);
    const total = formatMoney(unit * quantity, this.dataset.moneyFormat ?? '${{amount}}');
    if (this.textContent !== total) this.textContent = total;
  };
}

/**
 * Formats cents with the shop's money format (the common Shopify placeholders).
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  /** @param {number} value @param {number} decimals @param {string} thousands @param {string} decimal */
  const number = (value, decimals, thousands, decimal) => {
    const [whole = '0', fraction] = (value / 100).toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return number(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return number(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return number(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return number(cents, 2, "'", '.');
      default:
        return number(cents, 2, ',', '.');
    }
  });
}

if (!customElements.get('hl-add-price')) customElements.define('hl-add-price', AddPrice);
